import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import moment from 'moment';
import { Dispatch, RefObject } from 'react';
import { TFunction } from 'i18next';
import { 
  CREATE_PDF_REPORT,
  CREATE_PDF_REPORT_FAIL,
  CREATE_PDF_REPORT_SUCCESS
} from '../../actions/types';
import { AccessLevel, IReduxStateMachineDetails } from '../../types';
import { FormatHelper, PdfHelper } from '../../helpers';
import { performanceIndicatorColors, performanceIndicatorDefinitions, PerformanceIndicatorLevel } from '../../constants';
import { debug } from 'console';

export default class PdfReport {
  constructor(
    private readonly dispatch: Dispatch<any>,
    private readonly t: TFunction,
    private readonly isMobile: boolean,
    private readonly machineDetails: IReduxStateMachineDetails)
  {}

  // Helper-Funktion für Textkürzung
  private truncateText = (text: string | undefined, maxLength: number = 40): string => {
    if (!text) return '';
    return text.length > maxLength 
      ? text.substring(0, maxLength - 2) + '...' 
      : text;
  };

  private async renderToCanvas<THtmlElement extends HTMLElement>(
    elementRef: RefObject<THtmlElement>
  ): Promise<HTMLCanvasElement | undefined> {
    const elementToRender = elementRef.current;
    if (!elementToRender){
      console.log("renderToCanvas.!elementToRender")
      return Promise.resolve(undefined);
    }

    console.log("elementToRender");
    console.log(elementRef?.current?.id);
    console.log(elementToRender);

    return await html2canvas(elementToRender, {
      imageTimeout: 0,
      scale: 2,
      ignoreElements: (otherElement) =>
        !otherElement.contains(elementToRender) &&
        !elementToRender?.contains(otherElement) &&
        otherElement.localName !== 'head' &&
        otherElement.localName !== 'style',
    });
  }

  // TODO from KANM: Needs refactoring
  async createReport(
    fuelGaugesRef: RefObject<HTMLDivElement>,
    fuelLevelsRef: RefObject<HTMLDivElement>, 
    engineUtilizationGaugeRef: RefObject<HTMLDivElement>,
    engineUtilizationRef: RefObject<HTMLDivElement>,
    fuelConsumptionDisplayRef: RefObject<HTMLDivElement>,
    fuelConsumptionLineRef: RefObject<HTMLDivElement>,
    performanceIndicatorGaugeRef: RefObject<HTMLDivElement>,
    throughputDisplayRef: RefObject<HTMLDivElement>,
    throughputLineRef: RefObject<HTMLDivElement>,
    machineImgRef: RefObject<HTMLImageElement>,
    engineUtilizationGaugeHybridRef: RefObject<HTMLDivElement>,
    fuelConsumptionDisplayHybridRef: RefObject<HTMLDivElement>,
    engineCloseSideSettingRef: RefObject<HTMLDivElement>,
    engineCrusherDrivePressureRef: RefObject<HTMLDivElement>,
    showthroughput: boolean,
    hasJawPressure: boolean
  )
  {
    const dispatch = this.dispatch;
    const t = this.t;
    const isMobile = this.isMobile;

    const formatHelper = new FormatHelper(t);

    const {
      averageFuelConsumption,
      suggestedAddress,
      machine,
      search,
      totalHoursOfEngineOperation,
      totalHoursOfCrusherOperation,
      totalWeightLoad1,
      totalWeightLoad2,
      totalWeightLoad3,
      totalWeightLoad4,
      energySaved,
      co2Saved,
      dieselSaved,
      co2Emission,
    } = this.machineDetails;
    
    const currentlyAuthenticatedUserAccessLevel: AccessLevel = machine.currentlyAuthenticatedUserAccessLevel;
    // const isHybrid = machine.telemetrySnapshot?.isHybrid ?? false;
    const isHybrid = machine.isHybrid ?? false;
    const isThirdParty = machine.machineType.isThirdParty;

    dispatch({ type: CREATE_PDF_REPORT });

    console.log('***************************************');
    console.log('In createReport!! ');
    console.log('***************************************');
    // console.log('currentlyAuthenticatedUserAccessLevel : ' + currentlyAuthenticatedUserAccessLevel);
    
    try {
      const serialNumber = !!machine.serialNumber ? machine.serialNumber : '';
      const machineState = !!machine.state && !!machine.state.key ? machine.state.key : '-';

      const doc = new jsPDF('landscape');
      const reportTitle = `${t('report.machineReport')}`;
      const reportTimeframe = formatHelper.formatTimeframe(moment(search.startDate), moment(search.endDate), 'DD.MM.YYYY HH:mm');
      const reportFileName = `${t('report.report')} - ${serialNumber} - ${formatHelper.formatTimeframe(moment(search.startDate), moment(search.endDate), 'DD.MM.YYYY HHmm')}`;
      const reportCreationInformation = `${t('report.created')}: ${moment().format('DD.MM.YYYY HH:mm')}`;

      const workingHoursTillService = formatHelper.formatSuffixedNumber(machine?.nextService?.workingHoursTillService, t('machine.stats.hours'));
      const engineOperationTotal = formatHelper.formatSuffixedNumber(totalHoursOfEngineOperation, t('machine.stats.hours'));
      const crusherOperationTotal = formatHelper.formatSuffixedNumber(totalHoursOfCrusherOperation, t('machine.stats.hours'));

      // const energySavingsTxt : string = t('graph.energySavings');
      // const co2EmissionsTxt : string = t('co2Emissions');
      // const co2SavingsTxt : string = t('graph.co2Savings');

      const spsVersion = !!machine.spsProgramVersion ? machine.spsProgramVersion : '-';
      const displayVersion = !!machine.displayProgramVersion ? machine.displayProgramVersion : '-';

      const pdfTotalWeightLoad1 = formatHelper.formatUnitValue(totalWeightLoad1, t('machine.stats.t'));
      const pdfTotalWeightLoad2 = formatHelper.formatUnitValue(totalWeightLoad2, t('machine.stats.t'));
      const pdfTotalWeightLoad3 = formatHelper.formatUnitValue(totalWeightLoad3, t('machine.stats.t'));
      const pdfTotalWeightLoad4 = formatHelper.formatUnitValue(totalWeightLoad4, t('machine.stats.t'));

      const engineFuelConsumed = formatHelper.formatUnitValue(averageFuelConsumption.engineFuelConsumed, t('report.liter'));

      const projectConstructionSiteWithAuth =
        currentlyAuthenticatedUserAccessLevel === AccessLevel.Read ||
          currentlyAuthenticatedUserAccessLevel === AccessLevel.Write
          ? !!machine.projectConstructionSite ? machine.projectConstructionSite : t('accessLevel.placeholder')
          : t('accessLevel.placeholder');

      const pdfHelper = new PdfHelper(
        doc,
        serialNumber,
        reportTitle,
        reportTimeframe,
        reportCreationInformation,
        t('report.page'),
        t('report.accuracyNote'),
        isMobile
      );

      console.log('.. createPdfReportHeader');
      pdfHelper.createReportHeader()

      this.loadMachineImageData(
        machineImgRef.current?.src,
        machine.machineType.name,
        (imageData: HTMLImageElement, imageWidth: number, imageHeight: number) => {
          doc.addImage({
            imageData,
            x: 16,
            y: doc.internal.pageSize.getHeight() - 30 - imageHeight,
            w: imageWidth,
            h: imageHeight
          });
        },
        (imageWidth: number, imageHeight: number) => {
          doc.rect(
            16,
            doc.internal.pageSize.getHeight() - 30 - imageHeight,
            imageWidth,
            imageHeight);
        }
      );

      const lineHeight = isHybrid ? 7 : 8;
      const lineFormatter = (multiplier: number) => {
        const startY = 40;

        return startY + lineHeight * multiplier;
      };

      let curLine: number = 0;
      const columnWidth = (doc.internal.pageSize.getWidth() - 32) / 4;
      let labelY = 16;
      let valueY = labelY + columnWidth;

      // **************************************************
      // METADATA: serialNumber, owner, machineState, construction site, address, nextService
      doc
        .setFontSize(12)
        .setFontType('bold')
        .text(`${t('machine.serialNumber')}:`, labelY, lineFormatter(curLine + 1))
        .text(`${t('machine.machineState')}:`, labelY, lineFormatter(curLine + 2))
        .text(`${t('machine.owner')}:`, labelY, lineFormatter(curLine + 3));

      doc
        .setFontType('normal')
        .text(serialNumber, valueY, lineFormatter(curLine + 1))
        .text(machineState, valueY, lineFormatter(curLine + 2))
        .text(this.truncateText(machine.owner, 32), valueY, lineFormatter(curLine + 3));

      curLine += 3;

      // suggested address
      doc
        .setFontType('bold')
        .text(`${t('machine.suggestedAddress')}:`, labelY, lineFormatter(curLine + 1))
        .setFontType('normal')
        ;

      const suggestedAddressArray = suggestedAddress.split(', ');
      for (let element of suggestedAddressArray) {
        doc.text(element, valueY, lineFormatter(curLine + 1));
        curLine++;
      }

      doc
        .setFontType('bold')
        .text(`${t('machine.nextService')}:`, labelY, lineFormatter(curLine + 1))
        .setFontType('normal')
        .text(workingHoursTillService, valueY, lineFormatter(curLine + 1))
        ;
      curLine++;

      // // ==================== CONSTRUCTION SITE (Mehrzeiliger Text) ====================
      // if (currentlyAuthenticatedUserAccessLevel === AccessLevel.Write){
      //   const constructionSiteLabel = `${t('machine.constructionSite')}:`;
  
      //   doc.setFontType('bold')
      //     .text(constructionSiteLabel, labelY, lineFormatter(curLine + 1));

      //   // WICHTIG: Mehrzeiligen Text mit automatischer Umbruch
      //   const maxWidth = doc.internal.pageSize.getWidth() - valueY - 20; // Abstand zum rechten Rand

      //   const splitConstructionSite = doc.splitTextToSize(
      //     projectConstructionSiteWithAuth || '', 
      //     maxWidth
      //   );

      //   doc.setFontType('normal');

      //   splitConstructionSite.forEach((line: string, index: number) => {
      //     doc.text(line, valueY, lineFormatter(curLine + 1 + index));
      //   });

      //   // curLine um die Anzahl der Zeilen erh�hen
      //   curLine += splitConstructionSite.length;

      //   // doc
      //   // .setFontType('bold')
      //   // .text(`${t('machine.constructionSite')}:`, labelY, lineFormatter(curLine + 1))
      //   // .setFontType('normal')
      //   // .text(projectConstructionSiteWithAuth, valueY, lineFormatter(curLine + 1))
      //   // ;
      //   // curLine ++;
      // }


      // END of METADATA
      curLine = 0;
      labelY = 16 + 2.2 * columnWidth;
      valueY = labelY + columnWidth;

      // **************************************************
      // crusherOperation (Betriebsstunden), mainDischargeConveyor (Hauptaustrageband) + 2/3/4

      if (isHybrid && !isThirdParty) {
        if(t('report.crusherOperation2').length > 0){
          doc
            .setFontType('bold')
            .text(`${t('report.crusherOperation1')}:`, labelY, lineFormatter(curLine + 1))
            .text(`${t('report.crusherOperation2')}:`, labelY, lineFormatter(curLine + 2))
            .setFontType('normal')
            .text(crusherOperationTotal, valueY, lineFormatter(curLine + 2))
          
          curLine +=2;
        }
        else{
          doc
            .setFontType('bold')
            .text(`${t('report.crusherOperation')}:`, labelY, lineFormatter(curLine + 1))
            .setFontType('normal')
            .text(crusherOperationTotal, valueY, lineFormatter(curLine + 1))
          
          curLine ++;
        }
      }

      if(t('report.engineOperation2').length > 0){
        doc
          .setFontType('bold')
          .text(`${isHybrid
            ? t('report.engineOperation1')
            : t('report.crusherOperation1')
          }`, labelY, lineFormatter(curLine + 1))
          .text(`${isHybrid
            ? t('report.engineOperation2')
            : t('report.crusherOperation2')
          }:`, labelY, lineFormatter(curLine + 2))
          .text(`${t('report.engineFuelConsumed')}:`, labelY, lineFormatter(curLine + 3))
          .setFontType('normal')
          .text(engineOperationTotal, valueY, lineFormatter(curLine + 2))
          // Zeile 2 : leer
          .text(engineFuelConsumed, valueY, lineFormatter(curLine + 3))
          ;
        curLine += 3;
      }
      else{
        doc
          .setFontType('bold')
          .text(`${isHybrid
            ? t('report.engineOperation')
            : t('report.crusherOperation')
          }:`, labelY, lineFormatter(curLine + 1))
          .text(`${t('report.engineFuelConsumed')}:`, labelY, lineFormatter(curLine + 2))
          .setFontType('normal')
          .text(engineOperationTotal, valueY, lineFormatter(curLine + 1))
          .text(engineFuelConsumed, valueY, lineFormatter(curLine + 2))
          ;
        curLine += 2;
      }

      if (!isThirdParty) {
        if(t('report.mainDischargeConveyor2').length > 0) {
          doc
            .setFontType('bold')
            .text(`${t('report.mainDischargeConveyor')}`, labelY, lineFormatter(curLine + 1))
            .text(`${t('report.mainDischargeConveyor2')}:`, labelY, lineFormatter(curLine + 2))
            .setFontType('normal')
            .text(pdfTotalWeightLoad1, valueY, lineFormatter(curLine + 2));
            curLine += 2;
        }
        else {
          doc
            .setFontType('bold')
            .text(`${t('report.mainDischargeConveyor')}:`, labelY, lineFormatter(curLine + 1))
            .setFontType('normal')
            .text(pdfTotalWeightLoad1, valueY, lineFormatter(curLine + 1));
            curLine += 1;
        }

        if (machine.machineType.maxBeltScales && machine.machineType.maxBeltScales >= 2){
          doc
            .setFontType('bold')
            .text(`${t('report.secondDischargeConveyor')}:`, labelY, lineFormatter(curLine + 1))
            .setFontType('normal')
            .text(pdfTotalWeightLoad2, valueY, lineFormatter(curLine + 1));
          curLine ++;
        }

        if (machine.machineType.maxBeltScales && machine.machineType.maxBeltScales >= 3){
          doc
            .setFontType('bold')
            .text(`${t('report.thirdDischargeConveyor')}:`, labelY, lineFormatter(curLine + 1))
            .setFontType('normal')
            .text(pdfTotalWeightLoad3, valueY, lineFormatter(curLine + 1));
          curLine ++;
        }
        
        if (machine.machineType.maxBeltScales && machine.machineType.maxBeltScales >= 4){
          doc
            .setFontType('bold')
            .text(`${t('report.fourthDischargeConveyor')}:`, labelY, lineFormatter(curLine + 1))
            .setFontType('normal')
            .text(pdfTotalWeightLoad4, valueY, lineFormatter(curLine + 1));
          curLine ++;
        }
      } 

      // **************************************************
      // co2Emissions
      if (isHybrid) {
        if(t('report.dieselEngine2').length > 0){
          doc
            .setFontSize(12)
            .setFontType('bold')
            .text(`${t('report.electricDrive')}:`, labelY, lineFormatter(curLine + 1))
            .text(`${t('report.dieselEngine')}`, labelY, lineFormatter(curLine + 2))
            .text(`${t('report.dieselEngine2')}:`, labelY, lineFormatter(curLine + 3))
            .text(`${t('report.energySavings')}:`, labelY, lineFormatter(curLine + 4))
            .text(`${t('report.co2Emissions')}:`, labelY, lineFormatter(curLine + 5))
            .setFontType('normal')
            .text(formatHelper.formatUnitValue(energySaved, t('graph.kwh')), valueY, lineFormatter(curLine + 1))
            .text(formatHelper.formatUnitValue(co2Saved, t('graph.t') + 'CO'), valueY, lineFormatter(curLine + 3))
            .text(formatHelper.formatUnitValue(dieselSaved, t('graph.l')), valueY, lineFormatter(curLine + 4))
            .text(formatHelper.formatUnitValue(co2Emission, t('graph.t') + 'CO2'), valueY, lineFormatter(curLine + 5))
            ;

          curLine += 5;
        }
        else {
          doc
            .setFontSize(12)
            .setFontType('bold')
            .text(`${t('report.electricDrive')}:`, labelY, lineFormatter(curLine + 1))
            .text(`${t('report.dieselEngine')}:`, labelY, lineFormatter(curLine + 2))
            .text(`${t('report.energySavings')}:`, labelY, lineFormatter(curLine + 3))
            .text(`${t('report.co2Emissions')}:`, labelY, lineFormatter(curLine + 4))
            .setFontType('normal')
            .text(formatHelper.formatUnitValue(energySaved, t('graph.kwh')), valueY, lineFormatter(curLine + 1))
            .text(formatHelper.formatUnitValue(co2Saved, t('graph.t') + 'CO'), valueY, lineFormatter(curLine + 2))
            .text(formatHelper.formatUnitValue(dieselSaved, t('graph.l')), valueY, lineFormatter(curLine + 3))
            .text(formatHelper.formatUnitValue(co2Emission, t('graph.t') + 'CO2'), valueY, lineFormatter(curLine + 4))
            ;

          curLine += 4;
        }
      }

      doc
        .setFontType('bold')
        .text(`${t('machine.spsVersion')} :`, labelY, lineFormatter(curLine + 1))
        .setFontType('normal')
        .text(spsVersion, valueY, lineFormatter(curLine + 1))
        ;
      curLine++;

      doc
        .setFontType('bold')
        .text(`${t('machine.displayVersion')} :`, labelY, lineFormatter(curLine + 1))
        .setFontType('normal')
        .text(displayVersion, valueY, lineFormatter(curLine + 1))
        ;
      curLine++;

      // ==================== CONSTRUCTION SITE (Mehrzeiliger Text) ====================
      if (currentlyAuthenticatedUserAccessLevel === AccessLevel.Write){
        const constructionSiteLabel = `${t('machine.constructionSite')}:`;
  
        doc.setFontType('bold')
          .text(constructionSiteLabel, labelY, lineFormatter(curLine + 1));

        // WICHTIG: Mehrzeiligen Text mit automatischer Umbruch
        const maxWidth = doc.internal.pageSize.getWidth() - labelY - 20; // Abstand zum rechten Rand

        let splitConstructionSite = doc.splitTextToSize(
          projectConstructionSiteWithAuth || '', 
          maxWidth
        );
        // === Begrenzung auf maximal 6 Zeilen ===
        if (splitConstructionSite.length > 6) {
          splitConstructionSite = splitConstructionSite.slice(0, 6);
          
          // Letzte Zeile mit "..." enden lassen
          if (splitConstructionSite.length > 0) {
            splitConstructionSite[splitConstructionSite.length - 1] += '...';
          }
        }

        doc.setFontType('normal');

        splitConstructionSite.forEach((line: string, index: number) => {
          doc.text(line, labelY, lineFormatter(curLine + 2 + index));
        });

        // curLine um die Anzahl der Zeilen erh�hen
        curLine += splitConstructionSite.length;

        // doc
        // .setFontType('bold')
        // .text(`${t('machine.constructionSite')}:`, labelY, lineFormatter(curLine + 1))
        // .setFontType('normal')
        // .text(projectConstructionSiteWithAuth, valueY, lineFormatter(curLine + 1))
        // ;
        // curLine ++;
      }

      // **************************************************

      pdfHelper.createReportFooter();

      // Get all chart images
      const images: { [key: string]: HTMLCanvasElement | undefined } = {};
      ({
        // Throughput
        0: images.throughputDisplay,
        1: images.throughputLine,

        // Fuel Consumption
        2: images.fuelConsumptionDisplay,
        3: images.fuelConsumptionLine,

        // Engine Utilization Graphs
        4: images.engineUtilizationGauge,
        5: images.engineUtilization,

        // Fuel Levels
        6: images.fuelGauges,
        7: images.fuelLevels,

        // Performance Indicator
        8: images.performanceIndicatorGauge,

        9: images.engineCloseSideSettingLine,

        10: images.engineCrusherDrivePressureLine,
      } = await Promise.all([
        // Throughput 0/1
        (!isThirdParty && showthroughput ? this.renderToCanvas(throughputDisplayRef) : undefined),
        (!isThirdParty && showthroughput ? this.renderToCanvas(throughputLineRef) : undefined),

        // Fuel Consumption 2/3
        this.renderToCanvas(isHybrid ? fuelConsumptionDisplayHybridRef : fuelConsumptionDisplayRef),
        this.renderToCanvas(fuelConsumptionLineRef),

        // Engine Utilization Graphs 4/5
        this.renderToCanvas(isHybrid ? engineUtilizationGaugeHybridRef : engineUtilizationGaugeRef),
        this.renderToCanvas(engineUtilizationRef),

        // Fuel Levels 6/7
        this.renderToCanvas(fuelGaugesRef),
        this.renderToCanvas(fuelLevelsRef),

        // Performance Indicator 8
        this.renderToCanvas(performanceIndicatorGaugeRef),

        // Engine CloseSideSetting 9
        this.renderToCanvas(engineCloseSideSettingRef),

        // Engine CrusherDrivePressure 10
        (hasJawPressure ? this.renderToCanvas(engineCrusherDrivePressureRef) : undefined),
      ]));

      pdfHelper
        // Throughput
        .addGraphReportsPage(images.throughputDisplay, images.throughputLine)
        // Fuel Consumption
        .addGraphReportsPage(images.fuelConsumptionDisplay, images.fuelConsumptionLine)
        // Engine Utilization Graphs
        .addGraphReportsPage(images.engineUtilizationGauge, images.engineUtilization)
        // Engine CloseSideSetting
        .addGraphReportsPage(images.engineCloseSideSettingLine, null)
        // Engine CrusherDrivePressure
        .addGraphReportsPage(images.engineCrusherDrivePressureLine, null)
        // Fuel Levels
        .addGraphReportsPage(images.fuelGauges, images.fuelLevels)
        // Performance Indicator
        .addGraphReportsPage(images.performanceIndicatorGauge, undefined)
        // Page numbers
        .writeAllPageNumbers();

      /* ADD Performance Indicator Tooltip */

      const performanceIndicatorDescription = doc.splitTextToSize(
        t('graph.performanceIndicatorTooltip.description'),
        150);

      curLine = 5;

      doc
        .setFontSize(12)
        .setFontType('normal');

      for (const line of performanceIndicatorDescription){
        doc.text(line, 105, lineFormatter(curLine++))
      }

      curLine++;

      doc.text(t('graph.performanceIndicatorTooltip.thresholds') + ':', 105, lineFormatter(curLine++));

      for (let key of Object.keys(performanceIndicatorDefinitions))
      {
        const level = key as PerformanceIndicatorLevel;
        const definition = performanceIndicatorDefinitions[level];
        const color = performanceIndicatorColors[level].report;

        const prefix = `${t('graph.performanceIndicatorTooltip.engineLoad')} ${definition} = `;
        const dimensions = doc.getTextDimensions(prefix);
        const prefixWidth = dimensions.w ?? 55;

        doc
          .text(prefix, 110, lineFormatter(curLine))
          .setTextColor(color)
          .text(t(`graph.performanceIndicatorLevels.${level}`), 110 + prefixWidth, lineFormatter(curLine++))
          .setTextColor('black')
          ;
      }

      doc.save(`${reportFileName}.pdf`);
    } 
    catch (err) {
      console.error('########################################################');
      console.error('In createReport => ERROR!! ');
      console.error(err);
      console.error('########################################################');

      dispatch({ type: CREATE_PDF_REPORT_FAIL });
    }

    console.log('***************************************');
    console.log('Ende createReport!! ');
    console.log('***************************************');

    dispatch({ type: CREATE_PDF_REPORT_SUCCESS });
  }

  private loadMachineImageData(
    source: string | undefined,
    machineTypeName: string,
    onSuccess: (imageData: HTMLImageElement, width: number, height: number) => void,
    onError?: (width: number, height: number) => void,
    scale: number = 6
  ): void {
    if (!source){
      return;
    }

    const dimensions = { 
      width: 800 / scale, // Original width
      height: 450 / scale
    };
    const image = new Image();

    image.crossOrigin = 'anonymous';
    image.onerror = (error: any) => {
      console.error(`Error loading image for ${machineTypeName}`, error);

      onError && onError(dimensions.width, dimensions.height);
    };

    image.onload = () => {
      console.log(`Successfully loaded image for ${machineTypeName}`);

      onSuccess(image, dimensions.width, dimensions.height);
    };

    image.src = source;
  }
}
